import { memo } from 'react'
import cn from 'classnames'

import type { FloatingLabelProps } from './types'

import styles from './FloatingLabel.module.scss'

/**
 * Плавающий лейбл поля (основатель, п.10): пока пусто — текст по центру обычным кеглем
 * (замена «мигающему курсору»-имитации), при фокусе или значении — уезжает наверх и
 * уменьшается, значение остаётся под ним. Высота хоста при этом не меняется.
 *
 * Два слоя, а не один: внешний двигает, внутренний сжимает (почему именно так — в
 * `FloatingLabel.module.scss`). Анимируется только `transform`, поэтому переход идёт
 * без пересчёта раскладки.
 *
 * Переиспользуемый примитив — родитель (`TextInput` либо «поле-кнопка» вроде даты/времени/
 * города) должен:
 * 1. Быть `position: relative` и иметь фиксированную высоту.
 * 2. Держать значение внизу зоны (например `display: flex; flex-direction: column;
 *    justify-content: flex-end`), чтобы уехавшему наверх лейблу было куда деться.
 * 3. Объявить две переменные геометрии — `--field-font-size` (кегль поля в покое) и
 *    `--field-label-float-scale` (во сколько раз подпись сжимается наверху). Считать их
 *    внутри нельзя: токены — это CSS-переменные, а делить их в `calc()` браузеры не умеют.
 *
 * Живёт внутри `TextInput`: это единственное поле, где подпись работает плавающим
 * для полей-кнопок (дата/время/город), которые не являются нативным `<input>`.
 */
export const FloatingLabel = memo(function FloatingLabel(props: FloatingLabelProps) {
  const { text, isFloating, isFocused = false, hasError = false } = props

  return (
    <span className={cn(styles.root, isFloating && styles.isFloating)}>
      <span
        className={cn(
          styles.text,
          isFloating && styles.textFloating,
          isFocused && styles.isFocused,
          hasError && styles.hasError,
        )}
      >
        {text}
      </span>
    </span>
  )
})
